import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const apiUrl = import.meta.env.VITE_API_URL ?? '/api/v5';
const emailApi = apiUrl.replace(/\/v5\/?$/, '') + '/v5/email';

type MsgType = 'success' | 'info' | 'error';

export default function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const data = searchParams.get('data') ?? '';
  const actionParam = searchParams.get('action') ?? 'unsubscribe';

  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<MsgType>('error');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showResubscribe, setShowResubscribe] = useState(false);
  const [action, setAction] = useState(actionParam);
  const [ready, setReady] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!data) {
      setMessage('Invalid unsubscribe link.');
      setMessageType('error');
      setReady(true);
      return;
    }

    (async () => {
      try {
        const res = await axios.post(`${emailApi}/decode`, { data });
        setEmail(res.data.email ?? '');

        if (actionParam === 'resubscribe') {
          try {
            await axios.post(`${emailApi}/resubscribe`, { data });
            setMessage('You have been resubscribed successfully.');
            setMessageType('success');
            setAction('resubscribe');
          } catch {
            setMessage('You are already subscribed.');
            setMessageType('info');
            setAction('resubscribe');
          }
        } else {
          setShowConfirmation(true);
        }
      } catch {
        setMessage('Invalid or expired unsubscribe link.');
        setMessageType('error');
      }
      setReady(true);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUnsubscribe = async () => {
    setProcessing(true);
    try {
      await axios.post(`${emailApi}/unsubscribe`, { data });
      setMessage('You have been unsubscribed successfully.');
      setMessageType('success');
      setShowResubscribe(true);
    } catch {
      setMessage('You are already unsubscribed.');
      setMessageType('info');
      setShowResubscribe(true);
    }
    setShowConfirmation(false);
    setProcessing(false);
  };

  const handleResubscribe = async () => {
    setProcessing(true);
    try {
      await axios.post(`${emailApi}/resubscribe`, { data });
      setMessage('You have been resubscribed successfully.');
      setMessageType('success');
      setAction('resubscribe');
      setShowResubscribe(false);
    } catch {
      setMessage('You are already subscribed.');
      setMessageType('info');
      setAction('resubscribe');
    }
    setProcessing(false);
  };

  if (!ready) return null;

  const iconSuccess = <div style={{ width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 28, background: '#e8f5e9', color: '#2e7d32' }}>&#10003;</div>;
  const iconInfo    = <div style={{ width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 28, background: '#e3f2fd', color: '#1565c0' }}>&#8505;</div>;
  const iconError   = <div style={{ width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 28, background: '#fce4ec', color: '#c62828' }}>&#10007;</div>;
  const iconWarning = <div style={{ width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 28, background: '#fff3e0', color: '#e65100' }}>&#9993;</div>;

  const cardStyle: React.CSSProperties = {
    background: '#fff', borderRadius: 12, boxShadow: '0 2px 16px rgba(0,0,0,.08)',
    maxWidth: 480, width: '100%', margin: 20, padding: '48px 40px', textAlign: 'center',
  };
  const h1Style: React.CSSProperties = { fontSize: 22, fontWeight: 600, marginBottom: 12, color: '#1a1a1a' };
  const msgStyle: React.CSSProperties = { fontSize: 15, color: '#555', lineHeight: 1.6, marginBottom: 24 };
  const emailStyle: React.CSSProperties = { fontWeight: 600, color: '#333' };
  const btnBase: React.CSSProperties = { display: 'inline-block', padding: '12px 32px', borderRadius: 8, fontSize: 14, fontWeight: 600, textDecoration: 'none', cursor: 'pointer', border: 'none', transition: 'background .2s' };

  return (
    <div style={{ fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif", background: '#f5f7fa', color: '#333', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={cardStyle}>
        {showConfirmation ? (
          <>
            {iconWarning}
            <h1 style={h1Style}>Unsubscribe</h1>
            <p style={msgStyle}>
              Are you sure you want to unsubscribe?
              {email && <><br /><span style={emailStyle}>{email}</span></>}
            </p>
            <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, marginBottom: 24 }}>
              You will no longer receive marketing emails from us. This does not affect transactional emails such as appointment confirmations.
            </p>
            <button
              type="button"
              onClick={handleUnsubscribe}
              disabled={processing}
              style={{ ...btnBase, background: '#e53935', color: '#fff', opacity: processing ? 0.6 : 1 }}
            >
              {processing ? 'Processing...' : 'Yes, Unsubscribe Me'}
            </button>
          </>
        ) : (
          <>
            {messageType === 'success' ? iconSuccess : messageType === 'info' ? iconInfo : iconError}

            <h1 style={h1Style}>
              {action === 'resubscribe' && messageType !== 'error' ? 'Resubscribed' : 'Email Preferences'}
            </h1>

            <p style={msgStyle}>
              {message}
              {email && <><br /><span style={emailStyle}>{email}</span></>}
            </p>

            {showResubscribe && action !== 'resubscribe' && (
              <button
                type="button"
                onClick={handleResubscribe}
                disabled={processing}
                style={{ ...btnBase, background: '#00aff0', color: '#fff', opacity: processing ? 0.6 : 1 }}
              >
                {processing ? 'Processing...' : 'Resubscribe'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
